import { Table, Group, Text, ActionIcon, Box } from "@mantine/core"
import { useToggle } from "@mantine/hooks"
import { IconChevronLeft, IconChevronDown } from "@tabler/icons-react"
import { useTranslations } from "../../../../../utils/use-t"
import { localizeUnit } from "./localize-unit"
import { TimeAccountInPeriodDto } from "../../../../../@client/Theasoft_Absences_TimeAccountQueries"
import { useQueryGet } from "../../../../../typed-client/hooks/use-query-get"
import { GetCreditableValueForCurrentUser_Request } from "../../../../../@client/Theasoft_ApiServer_Absences"
import { ReactNode } from "react"

type VacationEntitlementProps = {
  timeAccountInfo: TimeAccountInPeriodDto
  request: GetCreditableValueForCurrentUser_Request | null
}

export function VacationEntitlementForCurrentUser({
  timeAccountInfo,
  request,
}: VacationEntitlementProps) {
  const {
    absences: { t },
    appServer: { t: appServerT },
  } = useTranslations()

  const unitLabel = localizeUnit(timeAccountInfo.unit, appServerT)

  const { data } = useQueryGet(
    "/api/Absence/GetCreditableValueForCurrentUser",
    request!,
    {
      enabled: request !== null && timeAccountInfo?.creditType === "Credit",
    }
  )
  const creditableValue = data ? `${data.value} ${unitLabel}` : null
  const remainingCreditsAfterRequest =
    data && typeof data.value === "number"
      ? // this propably should be computed on the server
        `${timeAccountInfo.availableCredits - data.value} ${unitLabel}`
      : null
  return (
    <VacationEntitlement
      timeAccountInfo={timeAccountInfo}
      additionalRows={[
        {
          label: <Text>{t("CreditableInSelectedPeriod")}</Text>,
          value: <Text>{creditableValue}</Text>,
        },
        {
          label: (
            <Text>
              {timeAccountInfo.formulaIsVacation
                ? t("RemainingVacation")
                : t("Remaining")}
            </Text>
          ),
          value: <Text>{remainingCreditsAfterRequest}</Text>,
        },
      ]}
    />
  )
}

type Row = { label: ReactNode; value: ReactNode }

export function VacationEntitlementForLeaveRequest({
  timeAccountInfo,
  creditableValue,
}: {
  timeAccountInfo: TimeAccountInPeriodDto | null
  creditableValue: number | null
}) {
  const ts = useTranslations()
  const unitLabel =
    timeAccountInfo && localizeUnit(timeAccountInfo.unit, ts.appServer.t)
  const additionalRows: Row[] =
    creditableValue && timeAccountInfo
      ? [
          {
            label: <Text>{ts.absences.t("AvailableBeforeLeaveRequest")}</Text>,
            value: (
              <Text>
                {timeAccountInfo.availableCredits + creditableValue} {unitLabel}
              </Text>
            ),
          },
          {
            label: <Text>{ts.absences.t("Creditable")}</Text>,
            value: (
              <Text>
                {creditableValue} {unitLabel}
              </Text>
            ),
          },
        ]
      : []

  return timeAccountInfo ? (
    <VacationEntitlement
      timeAccountInfo={timeAccountInfo}
      additionalRows={additionalRows}
    />
  ) : null
}

function VacationEntitlement({
  additionalRows,
  timeAccountInfo,
}: {
  additionalRows?: Row[]
  timeAccountInfo: TimeAccountInPeriodDto
}) {
  const data = timeAccountInfo
  const [showDetails, toggle] = useToggle()
  const {
    absences: { t: t_absences },
    appServer: { t },
  } = useTranslations()
  const unitLabel = localizeUnit(timeAccountInfo.unit, t)
  return (
    <Box>
      <Table variant="vertical" captionSide="bottom">
        <Table.Tbody>
          <Table.Tr>
            <Table.Th w={330}>{t_absences("Available")}</Table.Th>

            <Table.Td miw={150}>
              <Group justify="space-between">
                <Text>
                  {data.availableCredits} {unitLabel}
                </Text>
                <ActionIcon
                  variant="subtle"
                  c="dimmed"
                  size="xs"
                  onClick={() => toggle()}
                >
                  {showDetails ? <IconChevronDown /> : <IconChevronLeft />}
                </ActionIcon>
              </Group>
            </Table.Td>
          </Table.Tr>
          {showDetails ? (
            <>
              {data.details.map((v, i) => (
                <Table.Tr key={i}>
                  <Table.Th>
                    {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      t_absences(v.label as any)
                    }
                  </Table.Th>
                  <Table.Td>
                    <Text>
                      {v.value} {unitLabel}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </>
          ) : null}

          {additionalRows?.map((v, i) => (
            <Table.Tr key={i}>
              <Table.Th>{v.label}</Table.Th>
              <Table.Td>{v.value}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  )
}
